// Multi-client support for the SARVODAYA group.
//
// Two distinct ids, do not mix them up:
//
//   groupId  = _SARVODAYA  the GROUP. Its ONLY use is
//                          GET /api/OwnAppConfigs/GroupClientList/_SARVODAYA
//                          which lists the member firms:
//                          [{ groupClientId, wClientId, localUrl, firmName }, ...]
//
//   clientId = 5811997     Sarvodaya Agencies Udupi, a real client. Used by the
//                          CUSTOMER login, the customer area and the public
//                          pages - there is no firm to choose there.
//
// That list feeds the two firm dropdowns on the COMPANY side: one on the
// company login (a rep authenticates against a specific firm) and one on the
// reports screen (switch which firm's reports you are reading).
//
// The chosen firm is stored under SELECTED_KEY and is read by the company
// screens only. GlobalVariable.wcClient stays 5811997, so the customer side is
// never affected by what a company rep picked.
//
// Each firm has its own local server (localUrl). The api/OwnApp/* endpoints and
// the company reports live there, not on the central POPS API, so a screen that
// switches firm must switch localUrl with it.

import axios from 'axios';
import { env } from './env';

// Properties of this deployment, not secrets, so they are defaulted in code -
// the app must not break when a config file is missing.
const GROUP_ID_DEFAULT = '_SARVODAYA';
const CLIENT_ID_DEFAULT = '5811997';   // Sarvodaya Agencies Udupi

/**
 * The group. Only GroupClientList takes this.
 *
 * REACT_APP_WcCLIENT is accepted as a fallback because that is where the group
 * id (_SARVODAYA) actually lives in the existing .env - it predates the split
 * between "the group" and "a client".
 */
export const groupId =
    env.REACT_APP_GROUP_ID || env.REACT_APP_WcCLIENT || GROUP_ID_DEFAULT;

/**
 * Customer login, customer area, public pages. A REAL client id.
 *
 * Deliberately does NOT read REACT_APP_WcCLIENT: that holds the group id, and
 * sending a group id as a wclientid 404s every call.
 */
export const defaultClientId =
    env.REACT_APP_DEFAULT_CLIENT || CLIENT_ID_DEFAULT;

const baseUrl = env.REACT_APP_BASE_URL || 'https://popsv3.relysoft.in/';

/** Normalises one row of the GroupClientList response. */
const toClient = (row) => {
    if (!row) return null;
    const id = row.wClientId || row.wclientid || row.WClientId;
    if (!id) return null;
    return {
        wClientId: String(id),
        firmName: row.firmName || row.FirmName || String(id),
        localUrl: row.localUrl || row.LocalUrl || '',
        groupClientId: row.groupClientId || groupId,
    };
};

/** GET the firms belonging to the group. Resolves to a (possibly empty) list. */
export const fetchGroupClients = async () => {
    const url = baseUrl.replace(/\/$/, '') + '/api/OwnAppConfigs/GroupClientList/' + groupId;
    const res = await axios.get(url);
    const rows = Array.isArray(res.data) ? res.data : [];
    const clients = rows.map(toClient).filter(Boolean);
    if (!clients.length) {
        console.error('GroupClientList returned no usable firms for ' + groupId, res.data);
    }
    return clients;
};

// ---------------------------------------------------------------- company side

const SELECTED_KEY = 'selected-client';
const LOCAL_URL_KEY = 'LocalURL';

/** The firm a company rep signed in against, or null. */
export const getSelectedClient = () => {
    try {
        const raw = localStorage.getItem(SELECTED_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch (e) {
        console.error('Could not read the selected firm', e);
        return null;
    }
};

/** Its id, or '' when nothing has been chosen. */
export const getSelectedClientId = () => {
    const c = getSelectedClient();
    return (c && c.wClientId) || '';
};

export const getSelectedFirmName = () => {
    const c = getSelectedClient();
    return (c && c.firmName) || '';
};

/**
 * Store the choice, and point the local-server calls at the same firm.
 * The api/OwnApp/* endpoints and the company reports live on that firm's own
 * server, so writing the id without its localUrl would leave those screens
 * reading the previous firm with no error.
 */
export const setSelectedClient = (client) => {
    const c = toClient(client);
    if (!c) return null;
    localStorage.setItem(SELECTED_KEY, JSON.stringify(c));
    if (c.localUrl) {
        localStorage.setItem(LOCAL_URL_KEY, JSON.stringify(c.localUrl));
    } else {
        localStorage.removeItem(LOCAL_URL_KEY);
    }
    return c;
};

export const clearSelectedClient = () => {
    localStorage.removeItem(SELECTED_KEY);
    localStorage.removeItem(LOCAL_URL_KEY);
};

// ------------------------------------------------------- customer report side
//
// The customer report screens (ledger, pending bills, invoice download, order
// list, bounce list, bill tracking) each get a firm dropdown. That choice is
// kept under its own key so it is independent of both the company-side
// selection and GlobalVariable.wcClient, which stays the customer default.
//
// Ledger / pending bills / invoices / bounce list / bill tracking hit
// api/OwnApp/* on the FIRM'S OWN server, so the firm's localUrl must travel
// with its id. Order list hits the central API and needs the id only.

const REPORT_FIRM_KEY = 'report-firm';

/** The firm chosen on the customer report screens, or null. */
export const getReportFirm = () => {
    try {
        const raw = localStorage.getItem(REPORT_FIRM_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch (e) {
        console.error('Could not read the report firm', e);
        return null;
    }
};

/** Its client id, falling back to the customer default. */
export const getReportFirmId = () => {
    const f = getReportFirm();
    return (f && f.wClientId) || defaultClientId;
};

/** Its firm name, for headings. Empty when nothing has been chosen. */
export const getReportFirmName = () => {
    const f = getReportFirm();
    return (f && f.firmName) || '';
};

/**
 * Its local server. Falls back to the LocalURL the customer dashboard cached,
 * so a screen still works if it is opened before any firm has been picked.
 */
export const getReportFirmLocalUrl = () => {
    const f = getReportFirm();
    if (f && f.localUrl) return f.localUrl;
    try {
        const raw = localStorage.getItem(LOCAL_URL_KEY);
        return raw ? JSON.parse(raw) : '';
    } catch (e) {
        return '';
    }
};

export const setReportFirm = (client) => {
    const c = toClient(client);
    if (!c) return null;
    localStorage.setItem(REPORT_FIRM_KEY, JSON.stringify(c));
    return c;
};

export const clearReportFirm = () => {
    localStorage.removeItem(REPORT_FIRM_KEY);
};
